import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChartBarIcon, SpinnerGapIcon } from "@phosphor-icons/react";
import { fetchIndicators } from "../lib/api";
import { fmtPrice } from "../lib/format";

const toneColor = { good: "#00E599", bad: "#FF3366", warn: "#F5A623", neutral: "#8b8b8b" };
const TIMEFRAMES = ["5m", "1h", "4h"];

const SignalBadge = ({ s }) => (
  <span
    className="font-mono text-[11px] px-2.5 py-1 border"
    style={{ color: toneColor[s.tone] || "#8b8b8b", borderColor: (toneColor[s.tone] || "#8b8b8b") + "40" }}
  >
    {s.label}
  </span>
);

const Val = ({ label, value, color }) => (
  <div className="border border-white/10 p-3">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-mono font-bold text-sm mt-1" style={color ? { color } : {}}>{value}</div>
  </div>
);

const rsiColor = (r) => (r >= 70 ? "#FF3366" : r <= 30 ? "#00E599" : "#F5A623");

export const TechnicalIndicators = ({ address }) => {
  const [tf, setTf] = useState("1h");
  const { data, isLoading } = useQuery({
    queryKey: ["indicators", address, tf],
    queryFn: () => fetchIndicators(address, tf),
    staleTime: 240000,
  });

  return (
    <div data-testid="indicators-panel" className="border border-white/10 bg-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2">
          <ChartBarIcon size={20} className="text-neon" /> Technical Indicators
        </h2>
        <div data-testid="indicators-tf-switch" className="flex border border-white/10">
          {TIMEFRAMES.map((t) => (
            <button
              key={t}
              data-testid={`indicators-tf-${t}`}
              onClick={() => setTf(t)}
              className={`px-3 py-1 text-xs font-mono transition-colors ${
                tf === t ? "bg-neon/10 text-neon" : "text-muted-foreground hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-6 justify-center">
          <SpinnerGapIcon size={20} className="animate-spin text-neon" />
          <span className="font-mono text-xs">Crunching candles…</span>
        </div>
      ) : !data?.available ? (
        <p className="font-mono text-xs text-muted-foreground py-4">Not enough chart history yet to compute indicators for this token.</p>
      ) : (
        <div className="space-y-4">
          {data.signals?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {data.signals.map((s, i) => <SignalBadge key={i} s={s} />)}
            </div>
          )}

          {/* RSI meter */}
          {data.rsi != null && (
            <div>
              <div className="flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                <span>RSI (14)</span>
                <span className="font-mono font-bold" style={{ color: rsiColor(data.rsi) }}>{data.rsi}</span>
              </div>
              <div className="relative h-2 bg-white/5">
                <div className="absolute inset-y-0 left-[30%] w-px bg-white/20" />
                <div className="absolute inset-y-0 left-[70%] w-px bg-white/20" />
                <div className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 -ml-1.5 rounded-full" style={{ left: `${data.rsi}%`, background: rsiColor(data.rsi) }} />
              </div>
              <div className="flex justify-between text-[9px] text-muted-foreground mt-1 font-mono">
                <span>Oversold 30</span><span>70 Overbought</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Val label="EMA 9" value={fmtPrice(data.ema9)} />
            <Val label="EMA 21" value={fmtPrice(data.ema21)} />
            <Val label="VWAP" value={fmtPrice(data.vwap)} color={data.priceVsVwapPct >= 0 ? "#00E599" : "#FF3366"} />
            <Val label="MA 20" value={fmtPrice(data.sma20)} />
            <Val label="MA 50" value={fmtPrice(data.sma50)} />
            <Val label="Vol vs avg" value={`${data.volume?.ratio}x`} color={data.volume?.ratio >= 2 ? "#F5A623" : undefined} />
          </div>

          <p className="text-[10px] text-muted-foreground leading-relaxed">Computed from {data.timeframe} candles (GeckoTerminal). Technical signals are a research aid, not financial advice.</p>
        </div>
      )}
    </div>
  );
};
